import React, { useState, useEffect } from 'react';
import { X, FileText, Building2, Search, AlertCircle } from 'lucide-react';
import api from 'services/api';

const ConciliacaoManualModal = ({ 
  isOpen, 
  onClose, 
  itemOfx, 
  contasIxcDisponiveis = [],
  contasOfxDisponiveis = [],
  isPendenciaApi = false,
  onConfirm,
  conciliacaoId,
  onError
}) => {
  const [contaSelecionada, setContaSelecionada] = useState(null);
  const [contaOfxSelecionada, setContaOfxSelecionada] = useState(null);
  const [observacao, setObservacao] = useState('');
  const [termoPesquisa, setTermoPesquisa] = useState('');
  const [termoPesquisaOfx, setTermoPesquisaOfx] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isPendenciaApi && contasIxcDisponiveis.length > 0) {
        // Para pendências API, pré-selecionar a conta IXC
        setContaSelecionada(contasIxcDisponiveis[0]);
      } else {
        setContaSelecionada(null);
      }
      setContaOfxSelecionada(null);
      setObservacao('');
      setTermoPesquisa('');
      setTermoPesquisaOfx('');
    }
  }, [isOpen, isPendenciaApi, contasIxcDisponiveis]);

  if (!isOpen || !itemOfx) return null;

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data) => {
    if (!data) return '-';
    try {
      const matchBr = data.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (matchBr) {
        return data.split(' ')[0];
      }
      const matchIso = data.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (matchIso) {
        return `${matchIso[3]}/${matchIso[2]}/${matchIso[1]}`;
      }
      const date = new Date(data);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('pt-BR');
      }
      return data;
    } catch {
      return data;
    }
  };

  // Determinar tipo da transação OFX (apenas se não for pendência API)
  const tipoOfx = itemOfx?.tipo || (itemOfx?.valor >= 0 ? 'CREDIT' : 'DEBIT');
  const isCreditoOfx = !isPendenciaApi && (tipoOfx === 'CREDIT' || itemOfx?.valor >= 0);
  const isDebitoOfx = !isPendenciaApi && (tipoOfx === 'DEBIT' || itemOfx?.valor < 0);

  // Filtrar contas IXC por termo de pesquisa e compatibilidade de tipo
  const contasFiltradas = contasIxcDisponiveis.filter((conta) => {
    // Para pendências API, não filtrar por tipo OFX (ainda não temos transação OFX selecionada)
    if (!isPendenciaApi) {
      // Verificar compatibilidade de tipo apenas para pendências OFX
      const isContaPaga = conta.debito_data || conta.valor_total_pago !== undefined;
      const isContaRecebida = !isContaPaga;
      
      // Crédito OFX só pode ser conciliado com conta recebida
      // Débito OFX só pode ser conciliado com conta paga
      if (isCreditoOfx && isContaPaga) {
        return false; // Crédito não pode ser conciliado com conta paga
      }
      if (isDebitoOfx && isContaRecebida) {
        return false; // Débito não pode ser conciliado com conta recebida
      }
    }

    // Filtrar por termo de pesquisa
    if (termoPesquisa.trim()) {
      const termo = termoPesquisa.trim().toLowerCase();
      const casa = [conta.nome, conta.fornecedor, conta.cliente]
        .some((campo) => String(campo || '').toLowerCase().includes(termo));
      if (!casa) {
        return false;
      }
    }

    return true;
  });

  // Filtrar transações OFX por tipo e termo de pesquisa quando for pendência API
  const transacoesOfxFiltradas = isPendenciaApi && contaSelecionada
    ? contasOfxDisponiveis.filter((ofx) => {
        const tipoOfxItem = ofx.tipo || (ofx.valor >= 0 ? 'CREDIT' : 'DEBIT');
        const isCreditoOfx = tipoOfxItem === 'CREDIT' || ofx.valor >= 0;
        const isDebitoOfx = tipoOfxItem === 'DEBIT' || ofx.valor < 0;
        
        // Verificar tipo da conta IXC
        const isContaPaga = contaSelecionada.debito_data || contaSelecionada.valor_total_pago !== undefined;
        const isContaRecebida = !isContaPaga;
        
        // Conta Paga (débito) só pode ser conciliada com transação OFX de débito
        // Conta Recebida (crédito) só pode ser conciliada com transação OFX de crédito
        if (isContaPaga && !isDebitoOfx) {
          return false; // Conta paga não pode ser conciliada com crédito OFX
        }
        if (isContaRecebida && !isCreditoOfx) {
          return false; // Conta recebida não pode ser conciliada com débito OFX
        }
        
        // Filtrar por termo de pesquisa
        if (termoPesquisaOfx.trim()) {
          const termo = termoPesquisaOfx.trim().toLowerCase();
          const casa = [ofx.descricao, ofx.nome]
            .some((campo) => String(campo || '').toLowerCase().includes(termo));
          if (!casa) {
            return false;
          }
        }
        
        return true;
      })
    : contasOfxDisponiveis;

  const handleConfirmar = async () => {
    if (!contaSelecionada) {
      return;
    }

    // Para pendências API, verificar se uma transação OFX foi selecionada
    if (isPendenciaApi && !contaOfxSelecionada) {
      alert('Por favor, selecione uma transação OFX para conciliar.');
      return;
    }

    // Validar compatibilidade de tipo (apenas para pendências OFX)
    if (!isPendenciaApi) {
      const isContaPaga = contaSelecionada.debito_data || contaSelecionada.valor_total_pago !== undefined;
      const isContaRecebida = !isContaPaga;
      
      if (isCreditoOfx && isContaPaga) {
        alert('Erro: Uma transação de CRÉDITO (entrada) só pode ser conciliada com uma conta RECEBIDA do IXC.');
        return;
      }
      
      if (isDebitoOfx && isContaRecebida) {
        alert('Erro: Uma transação de DÉBITO (saída) só pode ser conciliada com uma conta PAGA do IXC.');
        return;
      }
    }

    setLoading(true);
    try {
      const tipoContaIxc = contaSelecionada.debito_data || contaSelecionada.valor_total_pago !== undefined 
        ? 'paga' 
        : 'recebida';

      // Para pendências API, usar a transação OFX selecionada; caso contrário, usar itemOfx
      const transacaoOfx = isPendenciaApi ? contaOfxSelecionada : itemOfx;

      const payload = {
        id_conta_ixc: contaSelecionada.id,
        tipo_conta_ixc: tipoContaIxc,
        dados_ixc: contaSelecionada,
        fitid_ofx: transacaoOfx?.fitid || null,
        dados_ofx: transacaoOfx || itemOfx,
        valor: transacaoOfx?.valor ?? itemOfx?.valor ?? 0,
        data: transacaoOfx?.data || itemOfx?.data || new Date().toISOString().split('T')[0],
        nome_normalizado: contaSelecionada.nome || contaSelecionada.fornecedor || contaSelecionada.cliente || null,
        observacao: observacao.trim() || null
      };

      // Se não há conciliacaoId, apenas atualizar estado local
      if (!conciliacaoId) {
        onConfirm(payload);
        onClose();
        setLoading(false);
        return;
      }

      // Se há conciliacaoId, salvar no backend
      const response = await api.post(
        `/n8n/administracao/conciliacao-bancaria/${conciliacaoId}/conciliacao-manual`,
        payload
      );

      // Verificar primeiro se houve erro mesmo com status 200
      if (response.data && response.data.success === false) {
        const errorMessage = response.data.message || 'Erro ao adicionar conciliação manual';
        console.error('Erro na resposta:', response.data);
        if (onError) {
          onError(errorMessage);
        } else {
          alert(errorMessage);
        }
        return;
      }

      if (response.data && response.data.success) {
        onConfirm(response.data.data);
        onClose();
      } else {
        const errorMessage = response.data?.message || 'Erro ao adicionar conciliação manual';
        console.error('Resposta inesperada:', response.data);
        if (onError) {
          onError(errorMessage);
        } else {
          alert(errorMessage);
        }
      }
    } catch (err) {
      console.error('Erro ao adicionar conciliação manual:', err);
      console.error('Detalhes do erro:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.response?.data?.message
      });
      
      // Tratamento melhorado de erros
      let errorMessage = 'Erro ao adicionar conciliação manual. Tente novamente.';
      
      // Priorizar mensagem do servidor
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 403) {
        errorMessage = 'Acesso negado. Você não tem permissão para adicionar conciliações manuais. Entre em contato com o administrador do sistema.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Sessão expirada. Por favor, faça login novamente.';
      } else if (err.response?.status === 422) {
        const validationErrors = err.response.data?.errors;
        if (validationErrors) {
          const firstError = Object.values(validationErrors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : 'Dados inválidos. Verifique as informações e tente novamente.';
        } else {
          errorMessage = 'Dados inválidos. Verifique as informações e tente novamente.';
        }
      } else if (err.response?.status >= 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
      } else if (!err.response) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      }
      
      if (onError) {
        onError(errorMessage);
      } else {
        alert(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div 
          className="fixed inset-0 bg-black/40 transition-opacity" 
          onClick={onClose}
        />
        
        <div className="relative inline-block w-full max-w-4xl p-6 my-8 text-left align-middle bg-[color:var(--surface)] rounded-lg shadow-xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between pb-4 border-b border-[color:var(--border)]">
            <div>
              <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">
                Conciliação Manual
              </h3>
              <p className="text-sm text-[color:var(--text-muted)] mt-1">
                {isPendenciaApi 
                  ? 'Selecione a transação OFX correspondente à conta IXC'
                  : 'Selecione a conta do IXC correspondente à transação OFX'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text-muted)] rounded-full hover:bg-[color:var(--surface-muted)]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Dados da Transação OFX ou Conta IXC (para pendências API) */}
          {isPendenciaApi ? (
            <div className="mt-4 p-4 bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="h-5 w-5 text-[color:var(--accent)]" />
                <h4 className="font-semibold text-[color:var(--text-primary)]">Conta IXC (Pendência API)</h4>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-[color:var(--text-muted)]">Valor:</span>
                  <span className={`ml-2 font-medium ${itemOfx?.valor < 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                    {formatarMoeda(itemOfx?.valor || 0)}
                  </span>
                </div>
                <div>
                  <span className="text-[color:var(--text-muted)]">Data:</span>
                  <span className="ml-2 font-medium">{formatarData(itemOfx?.data)}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-[color:var(--text-muted)]">Descrição:</span>
                  <span className="ml-2 font-medium">{itemOfx?.descricao || itemOfx?.nome || '-'}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4 p-4 bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-5 w-5 text-[color:var(--accent)]" />
                <h4 className="font-semibold text-[color:var(--text-primary)]">Transação OFX</h4>
                <span className={`ml-2 px-2 py-0.5 text-xs font-semibold rounded-full ${
                  isCreditoOfx 
                    ? 'bg-emerald-500/15 text-emerald-600 border border-emerald-500/30' 
                    : 'bg-red-500/15 text-red-500 border border-red-500/30'
                }`}>
                  {isCreditoOfx ? 'CRÉDITO' : 'DÉBITO'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-[color:var(--text-muted)]">Valor:</span>
                  <span className={`ml-2 font-medium ${itemOfx?.valor < 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                    {formatarMoeda(itemOfx?.valor || 0)}
                  </span>
                </div>
                <div>
                  <span className="text-[color:var(--text-muted)]">Data:</span>
                  <span className="ml-2 font-medium">{formatarData(itemOfx?.data)}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-[color:var(--text-muted)]">Descrição:</span>
                  <span className="ml-2 font-medium">{itemOfx?.descricao || itemOfx?.nome || '-'}</span>
                </div>
                {itemOfx?.fitid && (
                  <div>
                    <span className="text-[color:var(--text-muted)]">FITID:</span>
                    <span className="ml-2 font-medium text-xs">{itemOfx.fitid}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Busca e Lista de Contas IXC - apenas para pendências OFX */}
          {!isPendenciaApi && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="h-5 w-5 text-[color:var(--accent)]" />
                <h4 className="font-semibold text-[color:var(--text-primary)]">Contas do IXC</h4>
              </div>

              {/* Campo de busca */}
              <div className="mb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]" />
                  <input
                    type="text"
                    placeholder="Pesquisar por nome, fornecedor ou cliente..."
                    value={termoPesquisa}
                    onChange={(e) => setTermoPesquisa(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-[color:var(--surface)] text-[color:var(--text-primary)] border border-[color:var(--border)] rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                  />
                </div>
              </div>

              {/* Lista de contas */}
              <div className="max-h-[400px] overflow-y-auto border border-[color:var(--border)] rounded-lg">
                {contasFiltradas.length === 0 ? (
                  <div className="p-8 text-center text-[color:var(--text-muted)]">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2 text-[color:var(--text-muted)]" />
                    <p className="font-medium mb-1">Nenhuma conta compatível encontrada</p>
                    <p className="text-xs">
                      {isCreditoOfx 
                        ? 'Uma transação de CRÉDITO só pode ser conciliada com contas RECEBIDAS do IXC.'
                        : 'Uma transação de DÉBITO só pode ser conciliada com contas PAGAS do IXC.'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-[color:var(--border)]">
                    {contasFiltradas.map((conta) => {
                      const valorConta = conta.valor_pago ?? conta.valor_recebido ?? conta.valor ?? 0;
                      const isSelected = contaSelecionada?.id === conta.id;
                      
                      return (
                        <button
                          key={conta.id}
                          onClick={() => setContaSelecionada(conta)}
                          className={`w-full p-4 text-left hover:bg-[color:var(--surface-muted)] transition-colors ${
                            isSelected ? 'bg-[color:var(--surface-muted)] border-l-4 border-[color:var(--accent)]' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-[color:var(--text-primary)]">
                                {conta.nome || conta.fornecedor || conta.cliente || 'Sem nome'}
                              </div>
                              <div className="mt-1 text-sm text-[color:var(--text-muted)]">
                                ID: {conta.id} | 
                                <span className={conta.debito_data || conta.valor_total_pago !== undefined 
                                  ? ' text-red-500 font-medium' 
                                  : ' text-emerald-500 font-medium'}>
                                  {conta.debito_data || conta.valor_total_pago !== undefined 
                                    ? ' Conta Paga' 
                                    : ' Conta Recebida'}
                                </span>
                              </div>
                              <div className="mt-1 text-xs text-[color:var(--text-muted)]">
                                {conta.data_pagamento && `Data: ${formatarData(conta.data_pagamento)}`}
                                {conta.data_vencimento && ` | Vencimento: ${formatarData(conta.data_vencimento)}`}
                              </div>
                            </div>
                            <div className="ml-4 text-right">
                              <div className="font-semibold text-[color:var(--text-primary)]">
                                {formatarMoeda(valorConta)}
                              </div>
                              {Math.abs(valorConta - Math.abs(itemOfx.valor ?? 0)) < 0.01 && (
                                <div className="text-xs text-emerald-500 mt-1">✓ Valor corresponde</div>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Lista de Transações OFX (apenas para pendências API) */}
          {isPendenciaApi && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-5 w-5 text-[color:var(--accent)]" />
                <h4 className="font-semibold text-[color:var(--text-primary)]">Selecione a Transação OFX</h4>
              </div>
              
              {/* Campo de busca para OFX */}
              <div className="mb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]" />
                  <input
                    type="text"
                    placeholder="Pesquisar por descrição ou nome..."
                    value={termoPesquisaOfx}
                    onChange={(e) => setTermoPesquisaOfx(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-[color:var(--surface)] text-[color:var(--text-primary)] border border-[color:var(--border)] rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                  />
                </div>
              </div>
              
              <div className="max-h-[300px] overflow-y-auto border border-[color:var(--border)] rounded-lg">
                {transacoesOfxFiltradas.length === 0 ? (
                  <div className="p-8 text-center text-[color:var(--text-muted)]">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2 text-[color:var(--text-muted)]" />
                    <p className="font-medium mb-1">Nenhuma transação OFX compatível encontrada</p>
                    <p className="text-xs">
                      {contaSelecionada && (contaSelecionada.debito_data || contaSelecionada.valor_total_pago !== undefined)
                        ? 'Uma conta PAGA só pode ser conciliada com transações OFX de DÉBITO.'
                        : 'Uma conta RECEBIDA só pode ser conciliada com transações OFX de CRÉDITO.'}
                    </p>
                  </div>
                ) : (
                  transacoesOfxFiltradas.map((ofx) => {
                  const isSelected = contaOfxSelecionada?.fitid === ofx.fitid;
                  const tipoOfxItem = ofx.tipo || (ofx.valor >= 0 ? 'CREDIT' : 'DEBIT');
                  const isCredito = tipoOfxItem === 'CREDIT' || ofx.valor >= 0;
                  
                  return (
                    <button
                      key={ofx.fitid || Math.random()}
                      onClick={() => setContaOfxSelecionada(ofx)}
                      className={`w-full p-4 text-left hover:bg-[color:var(--surface-muted)] transition-colors ${
                        isSelected ? 'bg-[color:var(--surface-muted)] border-l-4 border-[color:var(--accent)]' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-[color:var(--text-primary)]">
                            {ofx.descricao || ofx.nome || '-'}
                          </div>
                          <div className="mt-1 text-sm text-[color:var(--text-muted)]">
                            <span className={isCredito 
                              ? ' text-emerald-500 font-medium' 
                              : ' text-red-500 font-medium'}>
                              {isCredito ? 'CRÉDITO' : 'DÉBITO'}
                            </span>
                            {ofx.fitid && ` | FITID: ${ofx.fitid}`}
                          </div>
                          <div className="mt-1 text-xs text-[color:var(--text-muted)]">
                            {formatarData(ofx.data)}
                          </div>
                        </div>
                        <div className="ml-4 text-right">
                          <div className={`font-semibold ${ofx.valor < 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                            {formatarMoeda(ofx.valor)}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                  })
                )}
              </div>
            </div>
          )}

          {/* Campo de observação */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-[color:var(--text-primary)] mb-2">
              Observação (opcional)
            </label>
            <textarea
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-[color:var(--surface)] text-[color:var(--text-primary)] border border-[color:var(--border)] rounded-lg focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
              placeholder="Adicione uma observação sobre esta conciliação manual..."
            />
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-[color:var(--border)] flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[color:var(--text-primary)] bg-[color:var(--surface-muted)] rounded-lg hover:brightness-95"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmar}
              disabled={!contaSelecionada || loading || (isPendenciaApi && !contaOfxSelecionada)}
              className="px-4 py-2 text-sm font-medium text-white bg-[color:var(--accent)] rounded-lg hover:brightness-110 disabled:bg-[color:var(--surface-muted)] disabled:text-[color:var(--text-muted)] disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Salvando...
                </>
              ) : (
                'Confirmar Conciliação'
              )}
            </button>
          </div>

          {!conciliacaoId && (
            <div className="mt-4 p-3 bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-lg">
              <div className="flex items-center gap-2 text-[color:var(--text-muted)] text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>A conciliação manual será salva quando você salvar a conciliação principal.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConciliacaoManualModal;



